<script setup lang="ts">
    import { useI18n } from 'vue-i18n'

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    withDefaults(
        defineProps<{
            /** Headline; defaults to the generic "No items found". */
            title?: string
            /** What the page has to say about being empty, in its own words. */
            description?: string
            /** Glyph in the tile; the tray says "nothing here yet" without alarm. */
            icon?: string
        }>(),
        {
            title: undefined,
            description: undefined,
            icon: 'ri:inbox-line',
        },
    )
</script>

<template>
    <div class="pk-table-empty">
        <!-- An empty table is a normal state, not a warning: a neutral tray
             instead of the sad face, and no "Attention" above it. The alarmed
             tone stays in PkTableError, where it belongs. -->
        <div class="pk-table-empty__glyph" aria-hidden="true">
            <VvIcon :name="icon" />
        </div>
        <div class="pk-table-empty__text">
            <strong class="pk-table-empty__title">
                {{ title ?? $t('message.empty') }}
            </strong>
            <p v-if="description" class="pk-table-empty__description">
                {{ description }}
            </p>
        </div>
        <div v-if="$slots.actions" class="pk-table-empty__actions">
            <slot name="actions" />
        </div>
    </div>
</template>

<style lang="scss">
    .pk-table-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--spacing-md);
        width: 100%;
        padding: var(--spacing-xl) var(--spacing-md);
        text-align: center;
        line-height: var(--leading-normal);

        // The same tile as the page title (PjPageTitle) and the service
        // cards, one size up. Concentric: radius (12) = padding (12).
        &__glyph {
            display: grid;
            place-items: center;
            width: var(--spacing-48);
            height: var(--spacing-48);
            padding: var(--spacing-12);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-xl);
            background-color: var(--color-surface-1);
            color: var(--color-word-2);

            .vv-icon {
                width: 100%;
                height: 100%;
                font-size: var(--text-24);
            }
        }

        &__text {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);
            // Roughly a sentence per line: longer descriptions wrap instead of
            // stretching across a wide table.
            max-width: 36ch;
        }

        &__title {
            font-size: var(--text-14);
            font-weight: var(--font-semibold);
            color: var(--color-word-1);
            text-wrap: balance;
        }

        &__description {
            margin: 0;
            font-size: var(--text-14);
            color: var(--color-word-3);
            text-wrap: pretty;
        }

        // The copy explains, the action resolves: a wider step before it
        // separates the two roles.
        &__actions {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: var(--spacing-sm);
            margin-block-start: var(--spacing-sm);
        }

        // Once, on arrival: the glyph, the copy and the action settle in
        // sequence so the eye lands on the button last. Reduced motion keeps
        // the static state only.
        &__glyph,
        &__text,
        &__actions {
            animation: pk-table-empty-enter var(--duration-300) var(--ease-out)
                backwards;
        }

        &__text {
            animation-delay: 60ms;
        }

        &__actions {
            animation-delay: 120ms;
        }

        @media (prefers-reduced-motion: reduce) {
            &__glyph,
            &__text,
            &__actions {
                animation: none;
            }
        }
    }

    @keyframes pk-table-empty-enter {
        from {
            opacity: 0;
            transform: translateY(var(--spacing-8));
        }
    }
</style>
